import {UserInfo} from "@firebase/auth";
import {AuthProviderType} from "../../domain/providers";

export type User = {
  uid: string
  email?: string
  contentId?: string
  emailVerified: boolean
  provider: AuthProviderType
  providers?: SsoProvider[]
  displayName?: string
  photoUrl?: string
  getIdToken: (forceRefresh?: boolean) => Promise<string>
  creationTime?: Date
}

// verifyAccountsFromDate - we will only initially require verification on newly created accounts
// pre-existing accounts will be handled in a later phase
export const verifyAccountsFromDate = new Date("2026-01-16T00:00:00.000Z");

export const requiresVerification = (user: User) =>
  !!user.email
  && !user.emailVerified
  && !!user.creationTime
  && user.creationTime >= verifyAccountsFromDate;


export type SsoProvider = {
  provider: string,
  uid: string
}

export type UserMetadata = {
  createdAt?: string,
  lastLoginAt?: string,
  lastSignInTime?: string,
  creationTime?: string
}

export interface FirebaseUser {
  uid: string
  providerId: string
  email: string | null
  displayName: string | null
  photoURL: string | null
  emailVerified: boolean
  providerData: UserInfo[]
  metadata: UserMetadata
  getIdToken: (forceRefresh?: boolean) => Promise<string>
  getIdTokenResult: (forceRefresh?: boolean) => Promise<IdTokenResult>
}

export type IdTokenResult = {
  claims: {[key: string]: any}
  token: string
}

export type OnAuthChangedCallBack = (firebaseUser: FirebaseUser | null) => void;

type noop = () => void;

export interface Auth {
  onIdTokenChanged: (cb: OnAuthChangedCallBack) => noop
  signOut: () => Promise<void>
}

export type UserAccount = {
  email: string
  displayName: string
  provider: AuthProviderType
  photoUrl?: string
};

export class UserAccounts {
  private accounts = new Map<string, UserAccount>();
  constructor(accounts: UserAccount[] = []) {
    accounts.reverse().forEach(account => this.add(account));
  }

  list(): UserAccount[] {
    return Array.from(this.accounts.values()).reverse();
  }

  add(account: UserAccount) {
    this.accounts.set(account.email, account);
  }

  remove(email: string) {
    this.accounts.delete(email);
  }

  isEmpty(): boolean {
    return this.accounts.size === 0;
  }
}
